import { useEffect, useRef } from "react";
import { postDashboardPresence } from "@/lib/api/account-pairs";

/**
 * Tells the server "I'm actively viewing these pairs" so the cycler
 * keeps each pair's bots awake. Fires every 30 s while the tab is
 * foregrounded and at least one pair id is in scope; pauses on
 * `document.visibilitychange → hidden` and on unmount.
 *
 * `pairIds` accepts an `account_pairs.slug`/`id` (preferred — used by
 * the /pairs detail pages) OR a child `pairs.slug`/`id` (the
 * /terminal/:slug slug is per-symbol; the server resolves it to the
 * parent account_pair).
 *
 * Pass an empty array to disable.
 */
const PING_INTERVAL_MS = 30_000;

export function usePresencePinger(pairIds: string[]): void {
  // Keep the latest list in a ref so the interval reads it without
  // re-creating itself on every render. Tabs that swap pairs every
  // few seconds would otherwise drop the cadence.
  const idsRef = useRef<string[]>(pairIds);
  idsRef.current = pairIds;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const fire = (): void => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const ids = idsRef.current;
      if (ids.length === 0) return;
      void postDashboardPresence(ids).catch(() => {
        // Best-effort — sleep state is recomputed by the cron
        // heartbeat anyway. A failed ping just means a slightly
        // delayed wake.
      });
    };

    // Fire once on mount so a pair the user just landed on wakes
    // immediately, then on the regular 30 s cadence.
    fire();
    timer = setInterval(fire, PING_INTERVAL_MS);

    const onVisibility = (): void => {
      if (document.visibilityState === "visible") fire();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
